import { Box, Button, Chip, Paper, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { print, radius } from "src/design/tokens";
import { fontMono, fontStretch } from "src/design/typography";
import {
  AwardChip,
  DataTable,
  InlineAlert,
  Loader,
  LoadingButton,
} from "src/grading/components";
import type { Award } from "src/grading/components/AwardChip/AwardChip";
import { TierTile } from "src/grading/components/StatTile/StatTile";
import { usePermissions } from "src/grading/hooks/usePermissions";
import { PageHeader } from "src/grading/layout/PageHeader";
import { ApiError } from "src/models";
import type { AwardTier, RosterStudent } from "src/models/sessions";
import { useAppDispatch } from "src/store/hooks";
import {
  SessionDocument,
  useGetSessionDocumentsQuery,
  useLazyGetCertificateBatchQuery,
  useLazyGetScorecardQuery,
  useSendLettersMutation,
} from "src/store/reducers/documents/documentsApiSlice";
import {
  addBreadcrumb,
  clearBreadcrumbs,
} from "src/store/reducers/navigation/navigationSlice";
import {
  useGetReviewQuery,
  useGetSessionQuery,
} from "src/store/reducers/sessions/sessionsApiSlice";

const TIERS = ["presidential", "national", "participation"] as const;
const AWARD_ORDER: Award[] = [
  "presidential",
  "national",
  "participation",
  "incomplete",
  "pending",
  "absent",
  "excused",
];

/** Design `certTiers`: the Print pill takes the tier's badge colors. */
const tierColor = (theme: Theme, tier: (typeof TIERS)[number]) => {
  const p = theme.palette;
  switch (tier) {
    case "presidential":
      return { bg: p.tierPresidential, fg: p.onTier };
    case "national":
      return { bg: p.tierNational, fg: p.onTier };
    default:
      return { bg: p.tierParticipationBg, fg: p.tierParticipationFg };
  }
};

const messageOf = (e: unknown, fallback: string) => {
  const err = e as ApiError;
  return Array.isArray(err?.data?.message)
    ? err.data.message.join(", ")
    : (err?.data?.message ?? fallback);
};

interface Row {
  student: RosterStudent;
  award: Award;
  certificate?: SessionDocument;
  letter?: SessionDocument;
  supersededCertificate: boolean;
}

/** Print batch, per-student certificates and letters, scorecard, and email delivery when the district allows it. */
const CertificatesPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { hasPermissions } = usePermissions();
  const { data: session } = useGetSessionQuery(sessionId);
  const { data: review } = useGetReviewQuery(sessionId, {
    skip: !sessionId,
  });
  const {
    data: documentsView,
    isLoading,
    isError,
    refetch,
  } = useGetSessionDocumentsQuery(sessionId, {
    pollingInterval: session?.status === "finalized" ? 15_000 : 0,
  });
  const [getBatch, { isFetching: fetchingBatch }] =
    useLazyGetCertificateBatchQuery();
  const [getScorecard, { isFetching: fetchingScorecard }] =
    useLazyGetScorecardQuery();
  const [sendLetters, { isLoading: sending }] = useSendLettersMutation();
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const documents = documentsView?.documents;
  // FR-008: email only when the district delivers by email and this deployment can send it.
  const letterEmail = documentsView?.letterEmail ?? null;
  const canSend = hasPermissions("update", "document-worker-service:documents");
  const emailUnavailableReason =
    canSend && letterEmail && !letterEmail.available
      ? letterEmail.reason
      : null;
  const name = session?.name;

  useEffect(() => {
    if (!name) return undefined;
    dispatch(clearBreadcrumbs());
    dispatch(
      addBreadcrumb({ label: t("tabs.sessions"), path: "/grading/sessions" }),
    );
    dispatch(
      addBreadcrumb({ label: name, path: `/grading/sessions/${sessionId}` }),
    );
    dispatch(
      addBreadcrumb({
        label: t("certificates.title", {
          defaultValue: "Certificates & letters",
        }),
      }),
    );
    return () => {
      dispatch(clearBreadcrumbs());
    };
  }, [dispatch, name, sessionId, t]);

  const current = useMemo(
    () => (documents ?? []).filter((d) => !d.supersededAt),
    [documents],
  );
  const superseded = useMemo(
    () => (documents ?? []).filter((d) => d.supersededAt),
    [documents],
  );
  const byStudent = useMemo(() => {
    const map = new Map<
      string,
      { certificate?: SessionDocument; letter?: SessionDocument }
    >();
    for (const d of current) {
      if (!d.studentId) continue;
      const entry = map.get(d.studentId) ?? {};
      if (d.type === "certificate") entry.certificate = d;
      if (d.type === "family_letter") entry.letter = d;
      map.set(d.studentId, entry);
    }
    return map;
  }, [current]);

  const open = async (fetcher: () => Promise<{ data?: SessionDocument }>) => {
    setError(null);
    try {
      const { data } = await fetcher();
      if (data?.url) window.open(data.url, "_blank", "noopener");
    } catch (e) {
      setError(
        messageOf(
          e,
          t("certificates.notGenerated", {
            defaultValue: "Not generated yet. Finalize the session first.",
          }),
        ),
      );
    }
  };

  const send = async () => {
    setError(null);
    setSent(null);
    try {
      const r = await sendLetters(sessionId).unwrap();
      setSent(
        `${t("certificates.lettersSent", {
          defaultValue: "{{count}} letters sent",
          count: r.queued,
        })}${
          r.skipped
            ? `, ${t("certificates.lettersSkipped", {
                defaultValue: "{{count}} skipped (no guardian email on file)",
                count: r.skipped,
              })}`
            : ""
        }.`,
      );
      refetch();
    } catch (e) {
      setError(
        messageOf(
          e,
          t("certificates.sendError", {
            defaultValue: "Could not send letters",
          }),
        ),
      );
    }
  };

  if (isLoading || !session) return <Loader />;
  const finalized = session.status === "finalized";

  const rows: Row[] = session.rosterSnapshot
    .map((s) => {
      const docs = byStudent.get(s.studentId) ?? {};
      const result = review?.students.find((r) => r.studentId === s.studentId);
      const docTier: AwardTier | null =
        docs.certificate?.tier ?? docs.letter?.tier ?? null;
      const award: Award =
        docTier ??
        (result?.status === "absent" || result?.status === "excused"
          ? result.status
          : (result?.awardTier ?? "pending"));
      return {
        student: s,
        award,
        certificate: docs.certificate,
        letter: docs.letter,
        supersededCertificate: superseded.some(
          (d) => d.type === "certificate" && d.studentId === s.studentId,
        ),
      };
    })
    .sort(
      (a, b) =>
        AWARD_ORDER.indexOf(a.award) - AWARD_ORDER.indexOf(b.award) ||
        (a.student.lastName ?? "").localeCompare(b.student.lastName ?? "") ||
        a.student.firstName.localeCompare(b.student.firstName),
    );
  const countOf = (tier: (typeof TIERS)[number]) =>
    rows.filter((r) => r.award === tier).length;
  // Counts need the review view (preview) or generated certificates; never show placeholder zeros.
  const tallyReady =
    !!review || (finalized && current.some((d) => d.type === "certificate"));
  const sample = rows.find((r) => TIERS.some((tier) => tier === r.award));
  const awardName = (award: Award) =>
    t(`awards.${award}`, { defaultValue: award });

  return (
    <Box>
      <PageHeader
        title={t("certificates.title", {
          defaultValue: "Certificates & letters",
        })}
        description={t("certificates.description", {
          defaultValue:
            "Printed on the Council's official certificate PDFs, sorted by award and then last name.",
        })}
        actions={
          <>
            <LoadingButton
              variant="contained"
              loading={fetchingBatch}
              disabled={!finalized}
              onClick={() => open(() => getBatch(sessionId))}
              label={t("certificates.printAll", {
                defaultValue: "Print all certificates (PDF)",
              })}
            />
            <LoadingButton
              variant="outlined"
              color="secondary"
              loading={fetchingScorecard}
              disabled={!finalized}
              onClick={() => open(() => getScorecard(sessionId))}
              label={t("console.classScorecard")}
            />
            {canSend && letterEmail?.available && (
              <LoadingButton
                variant="outlined"
                color="secondary"
                data-testid="email-letters"
                loading={sending}
                disabled={!finalized}
                onClick={send}
                label={t("console.emailLetters")}
              />
            )}
          </>
        }
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {emailUnavailableReason ? (
          <Box data-testid="email-letters-unavailable">
            <InlineAlert severity="info">
              {emailUnavailableReason === "print-only"
                ? t("certificates.emailPrintOnly")
                : t("certificates.emailNotConfigured")}
            </InlineAlert>
          </Box>
        ) : null}
        {!finalized ? (
          <Box
            sx={(theme) => ({
              display: "flex",
              gap: 1.75,
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              p: "14px 18px",
              borderRadius: `${radius.panel}px`,
              bgcolor: theme.palette.surfaceAlt,
            })}
          >
            <Typography
              sx={{ fontSize: 14, fontWeight: 600, flex: "1 1 320px" }}
            >
              {t("certificates.previewNote", {
                defaultValue:
                  "Documents are generated when you finalize. Below is a preview from the scores so far.",
              })}
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={() =>
                history.push(`/grading/sessions/${sessionId}/review`)
              }
            >
              {t("certificates.goToReview", { defaultValue: "Go to Review" })}
            </Button>
          </Box>
        ) : null}
        {isError ? (
          <InlineAlert severity="warning">
            {t("certificates.loadError", {
              defaultValue:
                "Could not load documents. They may still be generating; this page refreshes every 15 seconds.",
            })}
          </InlineAlert>
        ) : null}
        {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
        {sent ? <InlineAlert severity="success">{sent}</InlineAlert> : null}
        {finalized && current.length === 0 ? (
          <InlineAlert severity="info">
            {t("certificates.generating", {
              defaultValue:
                "Documents are being generated. If nothing appears after a minute, reopen and re-finalize the session to retry.",
            })}
          </InlineAlert>
        ) : null}
        {superseded.length > 0 ? (
          <InlineAlert severity="warning">
            {t("certificates.superseded", {
              defaultValue:
                "{{count}} earlier documents were superseded by a re-finalize. Reprint anything already handed out.",
              count: superseded.length,
            })}
          </InlineAlert>
        ) : null}

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(3, minmax(0, 1fr))",
              lg: "repeat(3, minmax(0, 1fr)) minmax(0, 1.4fr)",
            },
          }}
        >
          {TIERS.map((tier) => (
            <TierTile
              key={tier}
              tier={tier}
              showIcon
              value={tallyReady ? countOf(tier) : "—"}
              action={
                <Button
                  variant="contained"
                  size="small"
                  disabled={!finalized || !tallyReady}
                  onClick={() => open(() => getBatch(sessionId))}
                  sx={(theme) => ({
                    bgcolor: tierColor(theme, tier).bg,
                    color: tierColor(theme, tier).fg,
                    "&:hover": { bgcolor: tierColor(theme, tier).bg },
                    "&.Mui-disabled": {
                      bgcolor: tierColor(theme, tier).bg,
                      color: tierColor(theme, tier).fg,
                    },
                  })}
                >
                  {t("certificates.printTier", {
                    count: tallyReady ? countOf(tier) : 0,
                    defaultValue: "Print {{count}}",
                  })}
                </Button>
              }
            />
          ))}
          <Box
            component="section"
            aria-label={t("certificates.preview", {
              defaultValue: "Certificate preview",
            })}
            data-testid="certificate-preview"
            sx={(theme) => ({
              position: "relative",
              gridColumn: { sm: "1 / -1", lg: "auto" },
              minHeight: 230,
              p: 1.75,
              borderRadius: `${radius.card}px`,
              bgcolor: print.paper,
              color: print.ink,
              boxShadow: theme.shadows[1],
            })}
          >
            <Box
              sx={(theme) => ({
                position: "absolute",
                inset: 14,
                border: `3px double ${theme.palette.tokens.primary}`,
                borderRadius: "6px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.25,
                p: 2.25,
                textAlign: "center",
              })}
            >
              <Typography
                component="span"
                sx={{
                  fontFamily: fontMono,
                  fontSize: 10.5,
                  fontWeight: 600,
                  opacity: 0.7,
                }}
              >
                {t("certificates.previewCaption", {
                  defaultValue:
                    "Official Council certificate PDF · fields filled by us",
                })}
              </Typography>
              <Typography
                sx={{
                  fontSize: 26,
                  fontWeight: 900,
                  lineHeight: 1,
                  fontStretch: fontStretch.display,
                }}
              >
                {sample
                  ? `${sample.student.firstName} ${sample.student.lastName}`
                  : "—"}
              </Typography>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                {sample
                  ? t("certificates.previewLine", {
                      defaultValue: "{{award}} Award · {{date}}",
                      award: awardName(sample.award),
                      date: session.date,
                    })
                  : t("certificates.noPreview", {
                      defaultValue: "No certificates yet.",
                    })}
              </Typography>
              <Box aria-hidden sx={{ display: "flex", gap: 0.5 }}>
                <Box sx={{ width: 26, height: 4, bgcolor: "error.main" }} />
                <Box
                  sx={(theme) => ({
                    width: 26,
                    height: 4,
                    bgcolor: print.paper,
                    boxShadow: `inset 0 0 0 1px ${theme.palette.divider}`,
                  })}
                />
                <Box sx={{ width: 26, height: 4, bgcolor: "primary.main" }} />
              </Box>
            </Box>
          </Box>
        </Box>

        <Paper sx={{ overflow: "hidden", minWidth: 0 }}>
          <DataTable<Row>
            aria-label={t("console.studentDocuments")}
            dense
            rows={rows}
            getRowKey={(r) => r.student.studentId}
            columns={[
              {
                key: "student",
                header: t("review.student"),
                render: (r) => (
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                    {r.student.lastName}, {r.student.firstName}
                  </Typography>
                ),
                width: "28%",
              },
              {
                key: "award",
                header: t("review.award"),
                render: (r) => <AwardChip award={r.award} size="sm" />,
              },
              {
                key: "certificate",
                header: t("results.certificate"),
                render: (r) =>
                  r.certificate ? (
                    <Button
                      size="small"
                      variant="outlined"
                      href={r.certificate.url}
                      target="_blank"
                      rel="noopener"
                    >
                      {t("certificates.openPdf", { defaultValue: "Open PDF" })}
                    </Button>
                  ) : r.supersededCertificate ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t("certificates.superseded", {
                        defaultValue: "Superseded",
                      })}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {r.award === "incomplete"
                        ? t("certificates.noneIncomplete", {
                            defaultValue: "No certificate (incomplete)",
                          })
                        : "—"}
                    </Typography>
                  ),
              },
              {
                key: "letter",
                header: t("results.familyLetter"),
                render: (r) =>
                  r.letter ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        href={r.letter.url}
                        target="_blank"
                        rel="noopener"
                      >
                        {t("certificates.letterEnEs", {
                          defaultValue: "Letter · EN / ES",
                        })}
                      </Button>
                      {r.letter.sentAt ? (
                        <Chip
                          size="small"
                          label={t("certificates.emailedOn", {
                            defaultValue: "emailed {{date}}",
                            date: new Date(
                              r.letter.sentAt,
                            ).toLocaleDateString(),
                          })}
                        />
                      ) : null}
                    </Box>
                  ) : (
                    "—"
                  ),
              },
              {
                key: "result",
                header: t("console.result"),
                align: "right",
                render: (r) => (
                  <Button
                    size="small"
                    onClick={() =>
                      history.push(
                        `/grading/sessions/${sessionId}/results/${r.student.studentId}`,
                      )
                    }
                    sx={{ fontSize: 13, px: 0.5 }}
                  >
                    {t("certificates.viewResult", {
                      defaultValue: "View result →",
                    })}
                  </Button>
                ),
              },
            ]}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default CertificatesPage;
